import { useEffect, useState } from 'react';
import { standardDate } from '../../utils/formatDate';

const DetailsTable = ({ moreDetails, serviceType }) => {
    const [detailedView, setDetailedView] = useState([]);

    const tableHeader = ['Package Name', 'Date', 'Booked Slots', 'Payout Date', 'Amount', 'Commission', 'Amount Paid'];

    // --------------------------------------------------
    // Get payout date records
    // --------------------------------------------------
    const getDateRecords = () => {
        if (!moreDetails) return [];

        if (serviceType === 'trip') {
            return moreDetails.tripIds || [];
        }

        if (serviceType === 'gu') {
            return moreDetails.guIds || [];
        }

        if (serviceType === 'act') {
            return moreDetails.actIds || [];
        }

        if (serviceType === 'camp') {
            return moreDetails.campIds || [];
        }

        return [];
    };

    // --------------------------------------------------
    // Get date ID from a date record
    // --------------------------------------------------
    const getDateId = (item) => {
        if (!item?._id) return null;

        return item._id.toString();
    };

    // --------------------------------------------------
    // Get slot records belonging to this date
    // --------------------------------------------------
    const getSlotsForDate = (item) => {
        if (!moreDetails?.slots?.length) {
            return [];
        }

        const dateId = getDateId(item);

        return moreDetails.slots.filter((slot) => {
            if (!slot?.guDatesId && !slot?.actDatesId) {
                return false;
            }

            const storedDateId = slot?.guDatesId?.toString() || slot?.actDatesId?.toString();

            return storedDateId === dateId;
        });
    };

    // --------------------------------------------------
    // Find actual slot from date document
    // --------------------------------------------------
    const getSlotDetails = (dateItem, payoutSlot) => {
        if (!dateItem?.slots?.length || !payoutSlot?.slotId) {
            return null;
        }

        const slotId = payoutSlot.slotId.toString();

        return dateItem.slots.find((slot) => slot?._id?.toString() === slotId);
    };

    // --------------------------------------------------
    // Display slot time
    // --------------------------------------------------
    const getSlotDisplay = (dateItem) => {
        if (serviceType === 'trip') {
            return dateItem?.bookedSlots ?? '0';
        }

        const payoutSlots = getSlotsForDate(dateItem);

        if (!payoutSlots.length) {
            return '-';
        }

        const slotNames = payoutSlots
            .map((payoutSlot) => {
                const slot = getSlotDetails(dateItem, payoutSlot);

                if (!slot) {
                    return payoutSlot?.slotId ? payoutSlot.slotId.toString() : '-';
                }

                const startTime = slot?.startTime || '';
                const endTime = slot?.endTime || '';

                if (startTime && endTime) {
                    return `${startTime} - ${endTime}`;
                }

                return startTime || endTime || '-';
            })
            .filter(Boolean);

        return slotNames.length ? slotNames.join(', ') : '-';
    };

    // --------------------------------------------------
    // Get date
    // --------------------------------------------------
    const getTripDate = (item) => {
        if (!item) return null;

        // TripDateDetails
        if (serviceType === 'trip') {
            return item?.startDate;
        }

        // GuDates / ActDates
        return item?.date || item?.startDate;
    };

    // --------------------------------------------------
    // Get package/activity/guide name
    // --------------------------------------------------
    const getPackageName = (item) => {
        if (serviceType === 'trip') {
            return item?.contentId?.title || 'N/A';
        }

        if (serviceType === 'gu') {
            return item?.contentId?.title || 'N/A';
        }

        if (serviceType === 'act') {
            return item?.contentId?.title || 'N/A';
        }

        if (serviceType === 'camp') {
            return item?.contentId?.title || 'N/A';
        }

        return 'N/A';
    };

    // --------------------------------------------------
    // Create table data
    // --------------------------------------------------
    useEffect(() => {
        const dateRecords = getDateRecords();

        if (!dateRecords.length) {
            setDetailedView([]);
            return;
        }

        const rows = dateRecords.map((item) => {
            return {
                ...item,
                displayPackageName: getPackageName(item),
                displayDate: getTripDate(item),
                displaySlots: getSlotDisplay(item)
            };
        });

        // --------------------------------------------------
        // Calculate total
        // --------------------------------------------------
        let totalAmount = 0;
        let commission = 0;
        let paidAmount = 0;

        dateRecords.forEach((item) => {
            const payoutDetails = item?.payoutDetails;

            totalAmount += Number(payoutDetails?.totalAmount) || 0;
            commission += Number(payoutDetails?.commission) || 0;
            paidAmount += Number(payoutDetails?.amount) || 0;
        });

        rows.push({
            isTotal: true,
            displayPackageName: 'Total',
            displayDate: null,
            displaySlots: '-',
            payoutDetails: {
                totalAmount,
                commission,
                amount: paidAmount,
                paymentDate: null
            }
        });

        setDetailedView(rows);
    }, [moreDetails, serviceType]);

    // --------------------------------------------------
    // Render
    // --------------------------------------------------
    return (
        <div className='tableContainer'>
            <table className='table'>
                <thead className='tableHead'>
                    <tr>
                        {tableHeader.map((head, headIndex) => (
                            <th key={headIndex}>{head}</th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {detailedView.map((row, rowIndex) => {
                        const tripDate = row?.displayDate;

                        return (
                            <tr key={rowIndex} className='tableBodyRow'>
                                {/* Package Name */}
                                <td>{row?.displayPackageName || 'N/A'}</td>

                                {/* Trip Date */}
                                <td>{tripDate ? standardDate(tripDate.split('T')[0]) : '-'}</td>

                                {/* Booked Slots */}
                                <td>{row?.displaySlots ?? '-'}</td>

                                {/* Payout Date */}
                                <td>
                                    {row?.payoutDetails?.paymentDate
                                        ? standardDate(row.payoutDetails.paymentDate.split('T')[0])
                                        : '-'}
                                </td>

                                {/* Amount */}
                                <td>₹{row?.payoutDetails?.totalAmount ?? '0'}</td>

                                {/* Commission */}
                                <td>₹{row?.payoutDetails?.commission ?? '0'}</td>

                                {/* Amount Paid */}
                                <td>₹{row?.payoutDetails?.amount ?? '0'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* --------------------------------------------------
                Mobile / Responsive Cards
            -------------------------------------------------- */}
            <div className='responsiveCardContainer'>
                {detailedView.map((row, rowIndex) => {
                    const tripDate = row?.displayDate;

                    return (
                        <div key={rowIndex} className='responsiveCard'>
                            <div className='cardContent'>
                                <p>
                                    <strong>{tableHeader[0]}:</strong> {row?.displayPackageName || 'N/A'}
                                </p>

                                <p>
                                    <strong>{tableHeader[1]}:</strong>{' '}
                                    {tripDate ? standardDate(tripDate.split('T')[0]) : '-'}
                                </p>

                                <p>
                                    <strong>{tableHeader[2]}:</strong> {row?.displaySlots ?? '-'}
                                </p>

                                <p>
                                    <strong>{tableHeader[3]}:</strong>{' '}
                                    {row?.payoutDetails?.paymentDate
                                        ? standardDate(row.payoutDetails.paymentDate.split('T')[0])
                                        : '-'}
                                </p>

                                <p>
                                    <strong>{tableHeader[4]}:</strong> ₹{row?.payoutDetails?.totalAmount ?? '0'}
                                </p>

                                <p>
                                    <strong>{tableHeader[5]}:</strong> ₹{row?.payoutDetails?.commission ?? '0'}
                                </p>

                                <p>
                                    <strong>{tableHeader[6]}:</strong> ₹{row?.payoutDetails?.amount ?? '0'}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DetailsTable;
